import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  LogIn,
  LogOut,
  Crown,
  Sparkles,
  ShieldCheck,
  ArrowRight,
  CheckCircle2,
  XCircle,
  Loader2,
} from "lucide-react";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND}/api`;

// Axios must forward cookies for the session_token to reach the backend.
axios.defaults.withCredentials = true;

// ---------------- Auth context ----------------
const AuthContext = createContext(null);
export const useAuth = () => useContext(AuthContext);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [usage, setUsage] = useState({ plan: "anon", used_today: 0, limit: 1, remaining: 1 });
  const [loading, setLoading] = useState(true);

  const refetch = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/auth/me`);
      setUser(r.data.user || null);
      setUsage(r.data.usage);
    } catch (e) {
      console.debug("[auth] /me failed — treating as anonymous", e?.message);
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // CRITICAL: If returning from OAuth callback, skip the /me check.
    // AuthCallback will exchange the session_id and establish the session first.
    if (window.location.hash?.includes("session_id=")) {
      setLoading(false);
      return;
    }
    refetch();
  }, [refetch]);

  const logout = useCallback(async () => {
    try {
      await axios.post(`${API}/auth/logout`);
    } catch (e) {
      console.debug("[auth] logout call failed (session likely expired)", e?.message);
    }
    setUser(null);
    await refetch();
    toast.success("Signed out.");
  }, [refetch]);

  // Optimistically override usage after a classify/scan response so the header
  // updates without a second /auth/me round-trip.
  const applyUsage = useCallback((next) => {
    if (next) setUsage(next);
  }, []);

  // Memoise the context payload so consumers don't re-render on every parent
  // render — only when one of the actual state slices changes.
  const ctxValue = useMemo(
    () => ({ user, usage, loading, refetch, logout, applyUsage }),
    [user, usage, loading, refetch, logout, applyUsage],
  );

  return (
    <AuthContext.Provider value={ctxValue}>
      {children}
    </AuthContext.Provider>
  );
}

// ---------------- Login helper ----------------
// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
export function startGoogleLogin() {
  const redirectUrl = window.location.origin + "/";
  const target =
    "https://auth.emergentagent.com/?redirect=" + encodeURIComponent(redirectUrl);
  // Log to help diagnose environment-specific issues (visible in browser
  // devtools console). No sensitive data.
  console.info("[auth] starting Google sign-in", { redirectUrl, target });
  window.location.href = target;
}

export function LoginButton({ variant = "primary", className = "", label = "Sign up" }) {
  const cls = variant === "primary" ? "ts-btn-primary" : "ts-btn-ghost";
  return (
    <button
      type="button"
      onClick={startGoogleLogin}
      data-testid="login-button"
      className={`inline-flex items-center gap-2 ${cls} ${className}`}
    >
      <LogIn size={13} strokeWidth={1.6} />
      {label}
    </button>
  );
}

// ---------------- AuthCallback ----------------
// Detects #session_id=xxx from the OAuth redirect, exchanges it for a session
// cookie via POST /api/auth/session, then removes the hash and returns to /.
export function AuthCallback() {
  const location = useLocation();
  const navigate = useNavigate();
  const [status, setStatus] = useState("processing");
  const [errorMsg, setErrorMsg] = useState("");

  useEffect(() => {
    const hash = location.hash || window.location.hash || "";
    const m = hash.match(/session_id=([^&]+)/);
    if (!m) {
      navigate("/", { replace: true });
      return;
    }
    const sessionId = decodeURIComponent(m[1]);
    let cancelled = false;
    (async () => {
      try {
        const r = await axios.post(`${API}/auth/session`, { session_id: sessionId });
        if (cancelled) return;
        setStatus("success");
        toast.success(`Welcome, ${(r.data?.user?.name || "back").split(" ")[0]}.`);
        // Clear the hash BEFORE reloading. Using replace() (not assigning
        // .href to the same URL) guarantees a real page load, so AuthProvider
        // re-fetches /auth/me with the freshly-set cookie.
        window.location.replace("/");
      } catch (e) {
        if (cancelled) return;
        const detail =
          e?.response?.data?.detail || e?.message || "Unknown error contacting server.";
        console.error("[auth] session exchange failed", {
          sessionId,
          endpoint: `${API}/auth/session`,
          status: e?.response?.status,
          detail,
        });
        setStatus("error");
        setErrorMsg(String(detail));
        toast.error(`Sign-up failed: ${detail}`);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  return (
    <div className="min-h-screen flex items-center justify-center bg-[color:var(--ts-bg)] text-[color:var(--ts-text)]">
      <div className="max-w-md text-center px-6" data-testid="auth-callback-view">
        {status === "processing" && (
          <>
            <Loader2 size={26} strokeWidth={1.4} className="mx-auto text-[color:var(--ts-accent)] animate-spin" />
            <p className="mt-6 font-mono-alt text-[11px] tracking-[0.22em] uppercase text-[color:var(--ts-text-muted)]">
              Signing you in…
            </p>
          </>
        )}
        {status === "error" && (
          <>
            <XCircle size={26} strokeWidth={1.4} className="mx-auto text-[color:var(--ts-danger-text)]" />
            <div className="mt-6 font-mono-alt text-[11px] tracking-[0.32em] uppercase text-[color:var(--ts-danger-text)]">
              Sign-up failed
            </div>
            <p
              data-testid="auth-callback-error"
              className="mt-4 text-[14px] font-light text-[color:var(--ts-text)] leading-relaxed"
            >
              {errorMsg}
            </p>
            <div className="mt-8 flex flex-col sm:flex-row gap-3 justify-center">
              <button
                type="button"
                onClick={startGoogleLogin}
                className="ts-btn-primary inline-flex items-center justify-center gap-2"
              >
                <LogIn size={13} strokeWidth={1.6} /> Try again
              </button>
              <button
                type="button"
                onClick={() => window.location.replace("/")}
                className="ts-btn-ghost inline-flex items-center justify-center gap-2"
              >
                Back to the app
              </button>
            </div>
          </>
        )}
        {status === "success" && (
          <>
            <CheckCircle2 size={26} strokeWidth={1.4} className="mx-auto text-[color:var(--ts-success-text)]" />
            <p className="mt-6 text-[14px]">Loading your dashboard…</p>
          </>
        )}
      </div>
    </div>
  );
}

// ---------------- User menu (avatar + dropdown) ----------------
export function UserMenu({ onOpenUpgrade }) {
  const { user, logout } = useAuth();
  const [open, setOpen] = useState(false);
  if (!user) return null;
  const initials = (user.name || user.email || "U").trim().slice(0, 1).toUpperCase();
  return (
    <div className="relative" data-testid="user-menu">
      <button
        type="button"
        onClick={() => setOpen((s) => !s)}
        className="flex items-center gap-2.5 pl-2 pr-3 py-1.5 border border-[color:var(--ts-border)] hover:border-[color:var(--ts-accent)]/60 transition-colors"
        data-testid="user-menu-trigger"
      >
        <span className="w-6 h-6 rounded-full bg-[color:var(--ts-surface-raised)] text-[color:var(--ts-text)] flex items-center justify-center font-mono-alt text-[11px]">
          {user.picture ? (
            <img src={user.picture} alt="" className="w-6 h-6 rounded-full object-cover" />
          ) : (
            initials
          )}
        </span>
        <span className="font-mono-alt text-[10.5px] tracking-[0.16em] uppercase text-[color:var(--ts-text-muted)] max-w-[130px] truncate">
          {user.name || user.email}
        </span>
        {user.plan === "pro" && (
          <span className="font-mono-alt text-[9.5px] tracking-[0.22em] uppercase text-[color:var(--ts-accent)] flex items-center gap-1">
            <Crown size={10} strokeWidth={1.6} /> pro
          </span>
        )}
      </button>
      {open && (
        <>
          <div
            className="fixed inset-0 z-40"
            onClick={() => setOpen(false)}
          />
          <div
            className="absolute right-0 mt-2 w-56 z-50 ts-panel-raised p-2 shadow-lg"
            data-testid="user-menu-dropdown"
          >
            <div className="px-3 py-2 border-b border-[color:var(--ts-border)] mb-1">
              <div className="text-[12px] text-[color:var(--ts-text)] truncate">{user.email}</div>
              <div className="mt-0.5 font-mono-alt text-[9.5px] tracking-[0.22em] uppercase text-[color:var(--ts-text-muted)]">
                {user.plan === "pro" ? "Pro plan" : "Free plan"}
              </div>
            </div>
            {user.plan !== "pro" && onOpenUpgrade && (
              <button
                type="button"
                onClick={() => { setOpen(false); onOpenUpgrade(); }}
                className="w-full text-left flex items-center gap-2 px-3 py-2 text-[12.5px] text-[color:var(--ts-text)] hover:bg-[color:var(--ts-surface-raised)] transition-colors"
                data-testid="menu-upgrade"
              >
                <Crown size={12} strokeWidth={1.6} className="text-[color:var(--ts-accent)]" />
                Upgrade to Pro
              </button>
            )}
            <button
              type="button"
              onClick={() => { setOpen(false); logout(); }}
              className="w-full text-left flex items-center gap-2 px-3 py-2 text-[12.5px] text-[color:var(--ts-text)] hover:bg-[color:var(--ts-surface-raised)] transition-colors"
              data-testid="menu-logout"
            >
              <LogOut size={12} strokeWidth={1.6} />
              Sign out
            </button>
          </div>
        </>
      )}
    </div>
  );
}

// ---------------- Usage chip in header ----------------
export function UsageChip({ onOpenUpgrade }) {
  const { user, usage } = useAuth();
  if (!usage) return null;
  const plan = usage.plan;
  if (plan === "pro") {
    return (
      <div
        data-testid="usage-chip-pro"
        className="flex items-center gap-2 font-mono-alt text-[10.5px] tracking-[0.22em] uppercase text-[color:var(--ts-accent)]"
      >
        <Crown size={11} strokeWidth={1.6} />
        Pro · unlimited
      </div>
    );
  }
  const remaining = usage.remaining;
  const limit = usage.limit;
  const isLow = remaining <= 0;
  let label;
  if (user) {
    label = `${remaining}/${limit} scans left today`;
  } else if (remaining > 0) {
    // Anonymous — mirror the "N/3 scans left" pattern used for authed free.
    label = `${remaining}/${limit} free scans left`;
  } else {
    label = "Free scans used · sign up";
  }
  return (
    <div className="flex items-center gap-3">
      <div
        data-testid="usage-chip"
        className={`font-mono-alt text-[10.5px] tracking-[0.20em] uppercase ${
          isLow ? "text-[color:var(--ts-warn-text)]" : "text-[color:var(--ts-text-muted)]"
        }`}
      >
        {label}
      </div>
      {user && user.plan !== "pro" && (
        <button
          type="button"
          onClick={onOpenUpgrade}
          data-testid="header-upgrade-btn"
          className="font-mono-alt text-[10.5px] tracking-[0.22em] uppercase text-[color:var(--ts-accent)] hover:underline"
        >
          Upgrade
        </button>
      )}
    </div>
  );
}

// ---------------- Upgrade modal ----------------
export function UpgradeModal({ open, onClose, reason = "manual" }) {
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);

  if (!open) return null;

  const startCheckout = async () => {
    if (!user) {
      startGoogleLogin();
      return;
    }
    setLoading(true);
    try {
      const r = await axios.post(`${API}/payments/checkout`, {
        origin_url: window.location.origin,
      });
      window.location.href = r.data.checkout_url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not start checkout.");
      setLoading(false);
    }
  };

  const reasonCopy = {
    manual: "Get unlimited scans and skip the daily cap.",
    free_quota_exhausted: "You've used all 3 free scans today. Go Pro for unlimited access — starts instantly.",
    anon_quota_exhausted: "Sign up for 3 more free scans every day — or go straight to Pro for unlimited access.",
  }[reason] || "Upgrade to Pro for unlimited scans.";

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center bg-black/70 backdrop-blur-sm p-4"
      data-testid="upgrade-modal"
      onClick={onClose}
    >
      <div
        className="ts-panel-raised max-w-lg w-full p-10 relative"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          type="button"
          onClick={onClose}
          className="absolute top-4 right-4 text-[color:var(--ts-text-muted)] hover:text-[color:var(--ts-text)] transition-colors"
          data-testid="upgrade-close"
          aria-label="Close"
        >
          <XCircle size={18} strokeWidth={1.4} />
        </button>
        <div className="font-mono-alt text-[10.5px] tracking-[0.32em] uppercase text-[color:var(--ts-accent)]">
          Upgrade to Pro
        </div>
        <h2 className="font-serif-display text-4xl leading-[1.05] tracking-tight mt-4">
          Unlimited scans for <span className="italic text-[color:var(--ts-accent)]">$10</span>/month.
        </h2>
        <p className="mt-5 text-[14px] font-light text-[color:var(--ts-text-muted)] leading-relaxed">
          {reasonCopy}
        </p>
        <ul className="mt-8 space-y-3 text-[13.5px] font-light text-[color:var(--ts-text)]">
          {[
            "Unlimited article & URL classifications",
            "Unlimited camera scans across all pages",
            "Priority model access",
            "Instant cached re-analysis (no double-pay)",
          ].map((b) => (
            <li key={b} className="flex items-start gap-3">
              <CheckCircle2 size={14} strokeWidth={1.6} className="mt-0.5 shrink-0 text-[color:var(--ts-accent)]" />
              <span>{b}</span>
            </li>
          ))}
        </ul>
        <div className="mt-9 flex flex-col gap-3">
          <button
            type="button"
            onClick={startCheckout}
            disabled={loading}
            data-testid="checkout-button"
            className="ts-btn-primary inline-flex items-center justify-center gap-2 w-full"
          >
            {loading ? (
              <><Loader2 size={13} className="animate-spin" /> Preparing checkout…</>
            ) : user ? (
              <><Sparkles size={13} strokeWidth={1.6} /> Continue to secure checkout <ArrowRight size={13} /></>
            ) : (
              <><LogIn size={13} strokeWidth={1.6} /> Sign up to upgrade</>
            )}
          </button>
          <button
            type="button"
            onClick={onClose}
            className="ts-btn-ghost inline-flex items-center justify-center gap-2 w-full"
            data-testid="upgrade-cancel"
          >
            Maybe later
          </button>
        </div>
        <p className="mt-6 text-[11px] font-light italic text-[color:var(--ts-text-muted)] leading-relaxed text-center">
          Payments powered by Stripe. Cancel anytime by contacting support.
        </p>
      </div>
    </div>
  );
}

// ---------------- Sign-in wall (shown when anon has spent their 1 trial) ----------------
export function SignInWall({ open, onClose, onOpenUpgrade }) {
  if (!open) return null;
  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center bg-black/70 backdrop-blur-sm p-4"
      data-testid="signin-wall"
      onClick={onClose}
    >
      <div
        className="ts-panel-raised max-w-md w-full p-10 relative"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          type="button"
          onClick={onClose}
          className="absolute top-4 right-4 text-[color:var(--ts-text-muted)] hover:text-[color:var(--ts-text)] transition-colors"
          aria-label="Close"
        >
          <XCircle size={18} strokeWidth={1.4} />
        </button>
        <div className="font-mono-alt text-[10.5px] tracking-[0.32em] uppercase text-[color:var(--ts-accent)]">
          Free scans used
        </div>
        <h2 className="font-serif-display text-3xl leading-tight tracking-tight mt-4">
          Sign up to keep <span className="italic text-[color:var(--ts-accent)]">analyzing</span>.
        </h2>
        <p className="mt-5 text-[14px] font-light text-[color:var(--ts-text-muted)] leading-relaxed">
          You&apos;ve used all 3 of your free scans. Signing in with Google gives you
          3 more scans every day — or go Pro for unlimited.
        </p>
        <div className="mt-8 flex flex-col gap-3">
          <LoginButton label="Sign up with Google · 3 free scans/day" className="w-full justify-center" />
          <button
            type="button"
            onClick={onOpenUpgrade}
            className="ts-btn-ghost inline-flex items-center justify-center gap-2 w-full"
            data-testid="signin-wall-upgrade"
          >
            <Crown size={12} strokeWidth={1.6} />
            Skip trial · Upgrade to Pro
          </button>
        </div>
      </div>
    </div>
  );
}

// ---------------- Payment success (polls /payments/status) ----------------
export function PaymentSuccess() {
  const location = useLocation();
  const navigate = useNavigate();
  const { refetch } = useAuth();
  const [state, setState] = useState({ phase: "polling", tries: 0 });

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const sessionId = params.get("session_id");
    if (!sessionId) {
      navigate("/", { replace: true });
      return;
    }
    let cancelled = false;
    const maxAttempts = 20; // ~40s at 2s intervals
    const poll = async (attempt = 1) => {
      if (cancelled) return;
      try {
        const r = await axios.get(`${API}/payments/status/${sessionId}`);
        setState({ phase: r.data.payment_status === "paid" ? "success" : "polling", tries: attempt });
        if (r.data.payment_status === "paid") {
          await refetch();
          toast.success("Welcome to Pro! Unlimited scans unlocked.");
          setTimeout(() => navigate("/", { replace: true }), 2200);
          return;
        }
        if (r.data.status === "expired" || r.data.payment_status === "failed") {
          setState({ phase: "failed", tries: attempt });
          return;
        }
      } catch (e) {
        /* transient — swallow and retry next tick */
        console.debug("[payment] status poll transient error", e?.message);
      }
      if (attempt < maxAttempts) {
        setTimeout(() => poll(attempt + 1), 2000);
      } else {
        setState({ phase: "timeout", tries: attempt });
      }
    };
    poll();
    return () => { cancelled = true; };
  }, []);

  return (
    <div className="min-h-screen flex items-center justify-center bg-[color:var(--ts-bg)] text-[color:var(--ts-text)] px-6">
      <div className="max-w-md text-center" data-testid="payment-success-view">
        {state.phase === "polling" && (
          <>
            <Loader2 size={30} strokeWidth={1.4} className="mx-auto text-[color:var(--ts-accent)] animate-spin" />
            <h1 className="font-serif-display text-3xl mt-8 leading-tight">Confirming your payment…</h1>
            <p className="mt-4 text-[13.5px] font-light text-[color:var(--ts-text-muted)]">
              This usually takes a few seconds.
            </p>
          </>
        )}
        {state.phase === "success" && (
          <>
            <Crown size={30} strokeWidth={1.4} className="mx-auto text-[color:var(--ts-accent)]" />
            <div className="font-mono-alt text-[10.5px] tracking-[0.32em] uppercase text-[color:var(--ts-accent)] mt-6">
              You&apos;re on Pro
            </div>
            <h1 className="font-serif-display text-4xl mt-4 leading-tight">Unlimited scans <span className="italic">unlocked</span>.</h1>
            <p className="mt-5 text-[14px] font-light text-[color:var(--ts-text-muted)]">Redirecting you back to your dashboard…</p>
          </>
        )}
        {state.phase === "failed" && (
          <>
            <XCircle size={30} strokeWidth={1.4} className="mx-auto text-[color:var(--ts-danger-text)]" />
            <h1 className="font-serif-display text-3xl mt-8">Payment did not complete.</h1>
            <p className="mt-4 text-[13.5px] font-light text-[color:var(--ts-text-muted)]">Please try again from the upgrade dialog.</p>
            <button onClick={() => navigate("/")} className="ts-btn-ghost mt-8">Back to the app</button>
          </>
        )}
        {state.phase === "timeout" && (
          <>
            <ShieldCheck size={30} strokeWidth={1.4} className="mx-auto text-[color:var(--ts-warn-text)]" />
            <h1 className="font-serif-display text-3xl mt-8">Still confirming…</h1>
            <p className="mt-4 text-[13.5px] font-light text-[color:var(--ts-text-muted)]">
              Payment succeeded but our system is taking longer than usual to sync. Refresh in a minute and your plan will show as Pro.
            </p>
            <button onClick={() => navigate("/")} className="ts-btn-ghost mt-8">Back to the app</button>
          </>
        )}
      </div>
    </div>
  );
}

export function PaymentCancel() {
  const navigate = useNavigate();
  return (
    <div className="min-h-screen flex items-center justify-center bg-[color:var(--ts-bg)] text-[color:var(--ts-text)] px-6">
      <div className="max-w-md text-center" data-testid="payment-cancel-view">
        <XCircle size={30} strokeWidth={1.4} className="mx-auto text-[color:var(--ts-warn-text)]" />
        <h1 className="font-serif-display text-3xl mt-8">Upgrade cancelled.</h1>
        <p className="mt-4 text-[13.5px] font-light text-[color:var(--ts-text-muted)]">
          No charge was made. You can go back and continue on the free plan.
        </p>
        <button onClick={() => navigate("/")} className="ts-btn-primary mt-8">Back to the app</button>
      </div>
    </div>
  );
}
